import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";
import { useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

type InventoryDocKind = "stock-adjustment" | "stock-transfer";

const DOC_META: Record<
  InventoryDocKind,
  { moduleLabelKey: string; moduleHref: string; entityFallbackLabelKey: string }
> = {
  "stock-adjustment": {
    moduleLabelKey: "StockAdjustments",
    moduleHref: "/inventory/stock-adjustments",
    entityFallbackLabelKey: "StockAdjustment",
  },
  "stock-transfer": {
    moduleLabelKey: "StockTransfers",
    moduleHref: "/inventory/stock-transfers",
    entityFallbackLabelKey: "StockTransfer",
  },
};

/** Frozen crumbs for inventory doc detail/print screens. */
export function useFrozenInventoryDocBreadcrumbs(
  kind: InventoryDocKind,
  opts?: { print?: boolean },
) {
  const { id } = useParams();
  const meta = DOC_META[kind];
  const [entityTitle, setEntityTitle] = useState("");

  const captureLabel = useCallback((label?: string | null) => {
    setEntityTitle((label || "").trim());
  }, []);

  const detailsHref = id ? `/inventory/${kind}/${id}` : undefined;

  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    const crumbs: BreadcrumbItem[] = [
      { labelKey: "Inventory" },
      { labelKey: meta.moduleLabelKey, href: meta.moduleHref },
      {
        labelKey: meta.entityFallbackLabelKey,
        ...(entityTitle ? { label: entityTitle } : {}),
        href: opts?.print ? detailsHref : undefined,
      },
    ];
    if (opts?.print) {
      crumbs.push({ labelKey: "print" });
    } else {
      crumbs.push({ labelKey: "Details" });
    }
    return crumbs;
  }, [meta, entityTitle, detailsHref, opts?.print]);

  return { breadcrumbItems, captureLabel, entityTitle };
}
